/**
 * Клиенты: `GET /admin/customers`, карточка с балансом и историей баллов,
 * правка профиля, блокировка, выгрузка CSV и стирание данных.
 *
 * Баланс — это баллы, один балл равен одному суму. История приходит готовой
 * лентой движений, считать её самим нечего. Числа карточки — только то, что
 * отдаёт сервер: баланс из профиля и `total` отфильтрованных списков.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { parseMs as toMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { fetchOrders, type OrderStatus } from '../orders/api'
import { DONE_STATUSES, LOST_STATUSES } from './model'

type CustomerDto = components['schemas']['Customer']
type LedgerDto = components['schemas']['LedgerEntry']
type AddressDto = components['schemas']['SavedAddress']

/** Язык покупателя: API выбирает его по заголовку запроса (ru, uz, en). */
export type CustomerLang = 'ru' | 'uz' | 'en'

export interface Customer {
  id: number
  firstName: string
  lastName: string
  name: string
  phone: string
  gender: 'male' | 'female' | ''
  /** День рождения `YYYY-MM-DD`; пустая строка — не указан (В-07). */
  birth: string
  lang: CustomerLang
  /** Дата регистрации, мс. */
  registered: number
  balance: number
  /** Привязан ли Telegram: по нему видно, откуда клиент пришёл. */
  telegram: boolean
  /** Заблокирован: войти в приложение не может (BR-06). */
  blocked: boolean
  notifyPush: boolean
  /**
   * Когда аккаунт удалён — самим клиентом или сотрудником (R-114), мс; 0 — не
   * удалён. Данные удалённого остаются видны, но менять их уже нельзя.
   */
  deletedAt: number
  /** Когда персональные данные стёрты (В-22), мс; 0 — не стёрты. */
  anonymizedAt: number
}

export type LedgerType = NonNullable<LedgerDto['type']>

export interface LedgerEntry {
  id: number
  type: LedgerType
  /** Со знаком: плюс — начислено, минус — списано. */
  amount: number
  orderId: number
  at: number
}

export interface CustomerCard {
  customer: Customer
  balance: number
  ledger: LedgerEntry[]
}

export interface CustomerAddress {
  id: number
  label: string
  def: boolean
  used: number
}

export function toCustomer(dto: CustomerDto): Customer {
  const first = dto.first_name ?? ''
  const last = dto.last_name ?? ''
  return {
    id: dto.id ?? 0,
    firstName: first,
    lastName: last,
    name: `${first} ${last}`.trim(),
    phone: dto.phone ?? '',
    gender: dto.gender ?? '',
    birth: dto.birth_date ?? '',
    lang: dto.language ?? 'ru',
    registered: toMs(dto.registered_at),
    balance: dto.balance ?? 0,
    telegram: dto.telegram_user_id !== null && dto.telegram_user_id !== undefined,
    blocked: dto.is_blocked ?? false,
    notifyPush: dto.notify_push ?? false,
    deletedAt: toMs(dto.deleted_at),
    anonymizedAt: toMs(dto.anonymized_at),
  }
}

export function toLedger(dto: LedgerDto): LedgerEntry {
  return {
    id: dto.id ?? 0,
    type: dto.type ?? 'order_accrual',
    amount: dto.amount ?? 0,
    orderId: dto.order_id ?? 0,
    at: toMs(dto.created_at),
  }
}

export interface CustomerQuery {
  q?: string
  /** Без статуса — все, удалённые тоже (R-114). */
  status?: 'active' | 'blocked' | 'deleted' | ''
  page?: number
  /** Строк на странице; больше 100 сервер не отдаёт. */
  pageSize?: number
}

export interface CustomerPage {
  items: Customer[]
  total: number
}

/** Отбор без страниц: им же фильтруется и выгрузка в CSV. */
function toFilterQuery(query: CustomerQuery) {
  return { q: query.q || undefined, status: query.status || undefined }
}

function toQuery(query: CustomerQuery) {
  return { ...toFilterQuery(query), page: query.page ?? 1, page_size: query.pageSize ?? 10 }
}

export async function fetchCustomers(api: ApiClient, query: CustomerQuery): Promise<CustomerPage> {
  const page = await api.get<{ items?: CustomerDto[]; total?: number }>('/admin/customers', {
    query: toQuery(query),
  })
  return { items: (page.items ?? []).map(toCustomer), total: page.total ?? 0 }
}

/**
 * Выгрузка в Excel (`GET /admin/customers/export?format=xlsx`): тот же отбор,
 * что у таблицы, но без страниц — в файл идёт вся выборка, а не видимые строки.
 * Не CSV: его Excel открывал в кодировке Windows, и кириллица ломалась.
 */
export function exportCustomers(api: ApiClient, query: CustomerQuery): Promise<Blob> {
  return api.download('/admin/customers/export', {
    query: { ...toFilterQuery(query), format: 'xlsx' },
  })
}

/** Файл нужен по кнопке, а не при открытии экрана, — поэтому функция, а не запрос. */
export function useExportCustomers() {
  const api = useApi()
  return (query: CustomerQuery) => exportCustomers(api, query)
}

export function useCustomers(query: CustomerQuery) {
  const api = useApi()
  return useQuery({
    queryKey: keys.customers(toQuery(query)),
    queryFn: () => fetchCustomers(api, query),
    // Пока грузится следующая страница, прежняя остаётся на экране: таблица
    // не мигает скелетом на каждом листании.
    placeholderData: keepPreviousData,
  })
}

export function useCustomer(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.customer(id),
    queryFn: async () => {
      const dto = await api.get<{
        customer?: CustomerDto
        balance?: number
        ledger?: LedgerDto[]
      }>(`/admin/customers/${id}`)
      return {
        customer: toCustomer(dto.customer ?? {}),
        balance: dto.balance ?? 0,
        ledger: (dto.ledger ?? []).map(toLedger),
      } satisfies CustomerCard
    },
    enabled: id > 0,
  })
}

export function useCustomerAddresses(id: number, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.customerAddresses(id),
    queryFn: async () => {
      const page = await api.get<{ items?: AddressDto[] }>(`/admin/customers/${id}/addresses`)
      return (page.items ?? []).map<CustomerAddress>((a) => ({
        id: a.id ?? 0,
        label: a.label,
        def: a.is_default ?? false,
        used: toMs(a.used_at),
      }))
    },
    enabled,
  })
}

export interface ProfilePatch {
  firstName: string
  lastName: string
  gender: 'male' | 'female'
  lang: CustomerLang
  /** Пустая строка означает «дату рождения не трогаем». */
  birth: string
}

export function useSaveCustomer() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ProfilePatch }) =>
      api.patch<CustomerDto>(`/admin/customers/${id}`, {
        first_name: patch.firstName,
        last_name: patch.lastName,
        gender: patch.gender,
        language: patch.lang,
        birth_date: patch.birth || undefined,
      }),
    onSuccess: async (_data, { id }) => {
      await client.invalidateQueries({ queryKey: keys.customer(id) })
      await client.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/**
 * Сколько заказов клиента завершено и сколько не состоялось. Суммы по
 * заказам сервер не считает, а фильтр статуса у списка один: на каждый
 * статус — запрос в одну строку, из ответа берём только `total`.
 */
export interface OrderCounts {
  done: number
  lost: number
}

export function useCustomerOrderCounts(id: number, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.orders({ customer_id: id, count: 'done-lost' }),
    queryFn: async (): Promise<OrderCounts> => {
      const count = async (statuses: OrderStatus[]) => {
        const pages = await Promise.all(
          statuses.map((status) => fetchOrders(api, { customerId: id, status, pageSize: 1 })),
        )
        return pages.reduce((sum, page) => sum + page.total, 0)
      }
      const [doneTotal, lostTotal] = await Promise.all([count(DONE_STATUSES), count(LOST_STATUSES)])
      return { done: doneTotal, lost: lostTotal }
    },
    enabled,
  })
}

export function useBlockCustomer() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, blocked }: { id: number; blocked: boolean }) =>
      api.post<unknown>(`/admin/customers/${id}/block`, { blocked }),
    onSuccess: async (_data, { id }) => {
      await client.invalidateQueries({ queryKey: keys.customer(id) })
      await client.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/**
 * Стереть персональные данные (`DELETE /admin/customers/{id}`, В-22): имя,
 * телефон и адреса уходят, история заказов и баллов остаётся обезличенной.
 * Работает и для клиента, который удалил аккаунт сам.
 */
export function useEraseCustomer() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/admin/customers/${id}`),
    onSuccess: async (_data, id) => {
      await client.invalidateQueries({ queryKey: keys.customer(id) })
      await client.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/** Ссылка на регистрацию уходит клиенту по SMS — заводить его руками не нужно. */
export function useRegistrationLink() {
  const api = useApi()
  return useMutation({
    mutationFn: (phone: string) =>
      api.post<unknown>('/admin/customers/registration-link', { phone }),
  })
}
